import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { pick, lang2 } from "@/lib/localize";
import { useTranslation as useI18n } from "react-i18next";

const LAYOUT = [
  { span: "md:col-span-7", ratio: "aspect-[16/11]", offset: "" },
  { span: "md:col-span-5", ratio: "aspect-[4/5]", offset: "md:mt-24" },
  { span: "md:col-span-5", ratio: "aspect-[4/5]", offset: "" },
  { span: "md:col-span-7", ratio: "aspect-[16/11]", offset: "md:mt-24" },
  { span: "md:col-span-6", ratio: "aspect-[4/3]", offset: "" },
  { span: "md:col-span-6", ratio: "aspect-[4/3]", offset: "md:mt-16" },
];

const ApartmentCard = ({ apt, index }) => {
  const { t, i18n } = useI18n();
  const lang = lang2(i18n.language);
  const layout = LAYOUT[index % LAYOUT.length];

  return (
    <Reveal className={`col-span-12 ${layout.span} ${layout.offset}`} delay={0.05 * (index % 2)}>
      <Link
        to={`/apartamento/${apt.slug}`}
        data-testid={`apartment-card-${apt.slug}`}
        className="group block"
      >
        <div className={`relative overflow-hidden border border-line ${layout.ratio}`}>
          <img
            src={apt.photos[0]}
            alt={apt.name}
            loading="lazy"
            style={{ objectPosition: apt.cover_pos || "center" }}
            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute bottom-0 left-0 right-0 p-5 flex justify-between items-end translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
            <span className="text-sand text-xs uppercase tracking-[0.25em]">{t("gallery.view")}</span>
            <ArrowUpRight size={20} className="text-sand" />
          </div>
        </div>
        <div className="flex items-baseline justify-between mt-5 border-b border-line pb-5">
          <div>
            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-ink/45">
              <span
                className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium normal-case tracking-normal"
                style={{ background: apt.color, color: "#FDFBF7" }}
              >
                {index + 1}
              </span>
              {apt.district}
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-ink mt-1 group-hover:text-terracotta transition-colors duration-300">
              {apt.name}
            </h3>
            <p className="text-xs uppercase tracking-[0.15em] text-ink/50 mt-2">
              {apt.guests} {t("gallery.guests")} · {apt.bedrooms} {t("gallery.bedrooms")} · {apt.area} m²
            </p>
          </div>
          <p className="text-right shrink-0">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-ink/45">{t("gallery.from")}</span>
            <span className="font-serif text-2xl text-ink">{apt.price_per_night} €</span>
            <span className="text-xs text-ink/50"> / {t("gallery.night")}</span>
          </p>
        </div>
      </Link>
    </Reveal>
  );
};

export const Gallery = () => {
  const { t } = useTranslation();
  const [apartments, setApartments] = useState([]);

  useEffect(() => {
    api.get("/apartments").then((r) => setApartments(r.data)).catch(() => {});
  }, []);

  return (
    <section id="apartamentos" data-testid="gallery-section" className="py-24 md:py-36 bg-sand">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="md:flex items-end justify-between border-b border-line pb-10 mb-16 md:mb-24">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-terracotta mb-4">{t("gallery.kicker")}</p>
              <h2 className="font-serif text-4xl md:text-6xl text-ink leading-[1.02] max-w-xl">{t("gallery.title")}</h2>
            </div>
            <p className="text-ink/55 font-light mt-6 md:mt-0 max-w-xs text-base">{t("gallery.sub")}</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-12 gap-x-6 md:gap-x-10 gap-y-16">
          {apartments.map((apt, i) => (
            <ApartmentCard key={apt.slug} apt={apt} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
